import { get } from 'lodash-es';
import { emptyObject, emptyArray } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import { hasFreightInBulkSelection } from '../../components/bulk-update-menu/has-freight-in-bulk-selection';

class ShipmentConfigurationServiceClass {
  mapModelOptionsToRequest(options) {
    if (!options) {
      return undefined;
    }
    return {
      ...options,
      confirmation: get(options, 'confirmation.confirmationId') || get(options, 'confirmationEnumId'),
    };
  }

  shouldSendBulkUpdateInsuranceCalculationFlag(model) {
    const { shippingConfiguration, presetId, insureAmountOption } = model;
    if (!shippingConfiguration) {
      return false;
    }
    const didUserProvideAnInsuranceAmount =
      shippingConfiguration?.packages?.length > 0 && shippingConfiguration.packages[0]?.insuredValue?.value;
    const isFromAppliedPresetWithAnInsuranceProvider = !!presetId && !!shippingConfiguration.insuranceProvider;
    const isFromInsuranceActionButIsWithoutAmount =
      !presetId && !!shippingConfiguration.insuranceProvider && !didUserProvideAnInsuranceAmount && !insureAmountOption;

    return isFromAppliedPresetWithAnInsuranceProvider || isFromInsuranceActionButIsWithoutAmount;
  }

  hasPackageWithSetWeight(shippingConfig) {
    const packages = get(shippingConfig, 'packages', emptyArray);
    const package1 = packages[0];
    const packageWeight = get(package1, 'weight.weight');
    return packages.length > 0 && packageWeight !== null && packageWeight !== undefined && packageWeight >= 0;
  }

  packageIsUsingUnitAndValue(package1) {
    return package1.weight !== undefined && package1.weight.unit !== undefined && package1.weight.value >= 0;
  }

  trimBadData(data) {
    if (data.serviceId && data.serviceId === 'null') {
      const dataWithoutServiceId = { ...data };
      delete dataWithoutServiceId.serviceId;
      return dataWithoutServiceId;
    }
    return data;
  }

  mapModelToRequest(model) {
    if (!model) {
      return;
    }
    const shippingConfig = get(model, 'shippingConfiguration', emptyObject);
    let shippingConfiguration = {
      ...shippingConfig,
    };

    const hasPackageWithWeight = this.hasPackageWithSetWeight(shippingConfig);
    if (hasPackageWithWeight) {
      // TODO: if back-end starts supporting multi-package bulk update adjust this logic
      const package1 = get(shippingConfig, 'packages.0', emptyObject);
      // TODO: adjust for expected schema to support shipment-configuration-widget/weight-input, if adding metric support may need to revert for obj instead of raw weight (use newWeight instead)
      // we have a mix of object structures here, sometimes we pass { unit: something, value: something } instead of { weightUnit: something, weight: something }
      const isUsingUnitAndValue = this.packageIsUsingUnitAndValue(package1);
      if (!isUsingUnitAndValue) {
        package1.weight = {
          unit: get(package1, 'weight.weightUnit', 'Ounces'),
          value: get(package1, 'weight.weight', 0),
        };
      }
      shippingConfiguration = {
        ...shippingConfig,
        packages: [package1],
      };
    }

    const shouldSendBulkUpdateInsuranceCalculationFlag = this.shouldSendBulkUpdateInsuranceCalculationFlag(model);
    if (shouldSendBulkUpdateInsuranceCalculationFlag) {
      shippingConfiguration = {
        ...shippingConfig,
        calculateInsuranceValue: 'SumOfProducts',
      };
    }

    return this.trimBadData({
      ...shippingConfiguration,
      options: this.mapModelOptionsToRequest(shippingConfiguration.options),
    });
  }

  isFedexHal(requestedService) {
    // Following regex matches string starting with 'fedex' & ending with 'hal', case insensitive
    const regex = RegExp('^fedex.*HAL$', 'i');
    return !!requestedService && regex.test(requestedService.trim());
  }

  orderSelectionContainsHalService(state, fulfillmentPlanIds = []) {
    const selectedOrderIds = (fulfillmentPlanIds.length && fulfillmentPlanIds) || Global.selectSelectedOrdersIds(state) || [];
    const salesOrderIds = Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: selectedOrderIds,
    });
    return salesOrderIds.some((salesOrderId) => {
      const requestedService = Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'requestedService',
      });
      return this.isFedexHal(requestedService) || false;
    });
  }

  orderSelectionContainsShippedPendingFulfillmentOrCancelledShipments(state) {
    const selectedOrderIds = Global.selectSelectedOrdersIds(state) || [];
    return selectedOrderIds.some((fulfillmentPlanId) => {
      const orderGridStatus = Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId, property: 'orderGridStatus' });
      const targetStatusNames = [
        FulfillmentPlanOrderGridStatus.Shipped.name,
        FulfillmentPlanOrderGridStatus.Cancelled.name,
        FulfillmentPlanOrderGridStatus.PendingFulfillment.name,
      ];
      return targetStatusNames.includes(orderGridStatus);
    });
  }

  orderSelectionContainsFreightShipments(state) {
    const selectedOrderIds = Global.selectSelectedOrdersIds(state) || emptyArray;
    return hasFreightInBulkSelection(state, selectedOrderIds);
  }
}

export const ShipmentConfigurationService = new ShipmentConfigurationServiceClass();
